import React from "react";
import { Title, TitleProps, Box, useMantineTheme, Text, Stack } from "@mantine/core";

interface BenchHeadingProps extends TitleProps {
  title?: string;
  subheading?: string;
  showRedBar?: boolean;
  barColor?: string;
}

const BenchHeading: React.FC<BenchHeadingProps> = ({
  title = "Heading",
  subheading,
  showRedBar = true,
  barColor = "#ee374d",
  children,
  order = 1,
  fz,
  ...props
}) => {
  const theme = useMantineTheme();

  // Calculate bar dimensions based on order or font size
  const getBarDimensions = () => {
    // If explicit font size is provided
    if (fz) {
      if (fz === "xs") return { height: "16px", thickness: "1px", radius: "0.5px" };
      if (fz === "sm") return { height: "20px", thickness: "1.5px", radius: "0.75px" };
      if (fz === "md") return { height: "24px", thickness: "2px", radius: "1px" };
      if (fz === "lg") return { height: "28px", thickness: "2.5px", radius: "1.25px" };
      if (fz === "xl") return { height: "32px", thickness: "3px", radius: "1.5px" };
      if (fz === "h6") return { height: "18px", thickness: "1.2px", radius: "0.6px" };
      if (fz === "h5") return { height: "20px", thickness: "1.5px", radius: "0.75px" };
      if (fz === "h4") return { height: "22px", thickness: "1.8px", radius: "0.9px" };
      if (fz === "h3") return { height: "26px", thickness: "2.2px", radius: "1.1px" };
      if (fz === "h2") return { height: "30px", thickness: "2.8px", radius: "1.4px" };
      if (fz === "h1") return { height: "19px", thickness: "6.2px", radius: "2.6px" };
      return { height: "28px", thickness: "2.5px", radius: "1.25px" }; // default
    }

    // If order is provided
    switch (order) {
      case 1: return { height: "34px", thickness: "8px", radius: "6px" };
      case 2: return { height: "30px", thickness: "6px", radius: "6px" };
      case 3: return { height: "26px", thickness: "2.2px", radius: "1.1px" };
      case 4: return { height: "22px", thickness: "1.8px", radius: "0.9px" };
      case 5: return { height: "20px", thickness: "1.5px", radius: "0.75px" };
      case 6: return { height: "18px", thickness: "1.2px", radius: "0.6px" };
      default: return { height: "28px", thickness: "2.5px", radius: "1.25px" };
    }
  };

  const finalBarColor = barColor || theme.colors[theme.primaryColor][6];
  const { height, thickness, radius } = getBarDimensions();

  return (
    <Box style={{ display: "flex", alignItems: subheading ? "flex-start" : "center", gap: "10px" }}>
      {showRedBar && (
        <div
          style={{
            width: thickness,
            height: height,
            backgroundColor: finalBarColor,
            borderRadius: radius,
            flexShrink: 0,
            marginTop: subheading ? "4px" : 0
          }}
        />
      )}
      <Stack spacing={0}>
        <Title order={order} fz={fz} {...props}>
          {title || children}
        </Title>
        {subheading && (
          <Text size="xs" color="dimmed" sx={{ lineHeight: 1.2 }}>
            {subheading}
          </Text>
        )}
      </Stack>
    </Box>
  );
};

export default BenchHeading;
